import React, { useState, useEffect } from 'react';
import { MapPin, Users, AlertCircle, X, RefreshCw } from 'lucide-react';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://fleet.host/api'
  : '/api';

interface Place {
  place_id: number;
  place_name: string;
  num_of_vehicles: number;
}

interface GeofenceMonitorProps {
  onClose: () => void;
}

const GeofenceMonitor: React.FC<GeofenceMonitorProps> = ({ onClose }) => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<number | null>(null);
  const [placeVehicles, setPlaceVehicles] = useState<any[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const fetchPlacesStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-places-stats.php`);
      const data = await response.json();
      
      if (data.success) {
        // Handle both array and object response formats
        const placesData = data.data?.places || data.data || [];
        setPlaces(Array.isArray(placesData) ? placesData : []);
      } else {
        throw new Error(data.error || 'Failed to fetch places stats');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaceVehicles = async (placeId: number) => {
    setLoadingVehicles(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-place-vehicles.php?place_id=${placeId}`);
      const data = await response.json();
      
      if (data.success) {
        // Response is data.vehicles, not data.data.vehicles
        const vehicles = data.data?.vehicles || [];
        setPlaceVehicles(vehicles);
      } else {
        throw new Error(data.error || 'Failed to fetch place vehicles');
      }
    } catch (err) {
      console.error('Error fetching place vehicles:', err);
      setPlaceVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    fetchPlacesStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPlacesStats, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedPlace !== null) {
      fetchPlaceVehicles(selectedPlace);
    }
  }, [selectedPlace]);

  const totalVehicles = places.reduce((sum, place) => sum + place.num_of_vehicles, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-purple-600 to-purple-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">Geofence Monitor</h2>
                <p className="text-sm text-purple-100">Real-time vehicle locations in places</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchPlacesStats}
                disabled={loading}
                className="p-2 bg-purple-500 hover:bg-purple-400 rounded-lg transition-colors text-white"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-purple-500 hover:bg-purple-400 rounded-lg transition-colors text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {/* Summary Card */}
          <div className="mb-6 p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-purple-700 font-medium mb-1">Total Places</div>
                <div className="text-3xl font-bold text-purple-900">{places.length}</div>
              </div>
              <div>
                <div className="text-sm text-purple-700 font-medium mb-1">Vehicles in Places</div>
                <div className="text-3xl font-bold text-purple-900">{totalVehicles}</div>
              </div>
            </div>
          </div>

          {/* Places List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-2 flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : places.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-gray-500">
                No places configured
              </div>
            ) : (
              places.map((place) => (
                <button
                  key={place.place_id}
                  onClick={() => setSelectedPlace(
                    selectedPlace === place.place_id ? null : place.place_id
                  )}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedPlace === place.place_id
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className={`w-5 h-5 ${
                        place.num_of_vehicles > 0 ? 'text-purple-600' : 'text-gray-400'
                      }`} />
                      <span className="font-semibold text-gray-900">{place.place_name}</span>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                      place.num_of_vehicles > 0
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Users className="w-4 h-4" />
                      {place.num_of_vehicles}
                    </div>
                  </div>
                  
                  {/* Expanded vehicle list */}
                  {selectedPlace === place.place_id && (
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      {loadingVehicles ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
                        </div>
                      ) : placeVehicles.length > 0 ? (
                        <div className="space-y-2">
                          {placeVehicles.map((vehicle: any, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 p-2 bg-white rounded border border-purple-100"
                            >
                              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                              <span className="text-sm font-medium text-gray-900">
                                {vehicle.license_plate || 'Unknown'}
                              </span>
                              {vehicle.driver && (
                                <span className="text-xs text-gray-600">
                                  • {vehicle.driver.first_name} {vehicle.driver.last_name}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 text-center py-2">
                          No vehicles in this place
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeofenceMonitor;
